/**
 * GMA-1608 — CompassCare Web facelift DEMO dataset.
 *
 * Populates the command-center screens (Overview / Vitals / Clients) with
 * realistic sample data so the facelift is fully interactive WITHOUT any real
 * backend wired — used by the /web/* routes when REACT_APP_DEMO_DATA === "1"
 * (commandcenter.compasscare.app). Shapes match what the pages consume:
 * status (severity), livingEnv, vitals + baselines, trends (sparklines),
 * home/room, device count, contacts, last contact/update.
 */

// GMA-1624: the SITRep fixtures roll up through the REAL engine (cadence
// windows + overall status) so demo data can never drift from what the
// SitrepPage computes at render time.
import { windowMsForCadence, overallStatus } from "./noraSitrepModel";

// A deterministic 24h HR trend ending near `end` (for the sparklines).
function trend(seed, end, rising) {
  const pts = [];
  const base = end > 0 ? end : 72;
  for (let i = 0; i < 24; i += 1) {
    const wob = Math.sin((i + (seed % 7)) * 0.9) * 5 + Math.sin(i * 0.35 + (seed % 5)) * 3;
    let v = rising ? base - 22 + (i / 23) * 22 + wob * 0.6 : base + wob - 2;
    if (i === 23) v = base;
    pts.push(Math.round(v));
  }
  return pts;
}

// [name, status, livingEnv, home, room, hr, spo2, rr, temp, bp, activity, badge, devices, lastContact, agoMin, age]
const RAW = [
  ["Dorothy Alvarez", "critical", "group_home", "Maplewood House", "Rm 3", 92, 88, 24, 98.7, "142/88", "Resting", "Low SpO₂", 3, "Check-in failed", 1, 84],
  ["Marcus Webb", "critical", "independent", "Riverside Apartments", "Apt 4B", 104, 95, 20, 98.4, "138/86", "No movement 40m", "Fall Detected", 2, "Today 8:15 AM", 1, 79],
  ["Tommy Ruiz", "atRisk", "group_home", "Cedar Ridge Home", "Rm 2", 96, 98, 17, 98.5, "118/74", "Walking (off-site)", "Perimeter Exit", 2, "Today 9:02 AM", 1, 71],
  ["Priya Natarajan", "atRisk", "independent", "Harbor View Residence", "", 88, 96, 18, 100.8, "126/80", "Resting", "High Temp", 1, "Today 8:40 AM", 3, 68],
  ["Elena Vasquez", "atRisk", "independent", "2214 Kenwood Ave", "", 118, 96, 19, 98.9, "130/84", "Pacing", "High HR", 2, "Today 9:20 AM", 2, 74],
  ["Harold Kim", "caution", "independent", "388 Old Mill Ct", "", 71, 97, 14, 98.1, "124/78", "No movement 20m", "No Movement", 1, "Today 7:55 AM", 4, 82],
  ["Miriam Stein", "caution", "group_home", "Elm Street House", "Rm 1", 68, 97, 13, 98.2, "118/72", "Low", "Low Activity", 2, "Yesterday 6:40 PM", 9, 88],
  ["Deshawn Pruitt", "caution", "group_home", "Northgate Home", "Rm 2", 84, 99, 18, 97.9, "118/82", "Walking", "Missed Check-in", 1, "Yesterday 6:10 PM", 11, 76],
  ["Lucia Ferraro", "caution", "group_home", "Northgate Home", "Rm 7", 79, 97, 18, 98.1, "113/68", "Light activity", "Door Open", 2, "Today 8:30 AM", 5, 80],
  ["Walter Boone", "caution", "independent", "1740 Piedmont St", "", 79, 97, 15, 98.3, "158/96", "Resting", "Elevated BP", 1, "Today 7:20 AM", 6, 77],
  ["Sam Whitcomb", "caution", "independent", "77 Riverbend Dr", "", 72, 96, 14, 97.5, "119/71", "Light activity", "Hydration Watch", 1, "Today 8:05 AM", 7, 73],
  ["Renee Caldwell", "offline", "group_home", "Birchwood Commons", "Rm 1", 0, 0, 0, 0, "—", "Unknown", "No Signal", 1, "Yesterday 4:00 PM", 54, 85],
  ["Gary Okafor", "offline", "independent", "901 Tamarack Way", "", 0, 0, 0, 0, "—", "Unknown", "No Signal", 1, "Yesterday 5:30 PM", 38, 90],
  ["Deborah McCray", "normal", "group_home", "Maplewood House", "Rm 1", 64, 99, 15, 98.6, "116/68", "Resting", "Normal", 2, "Today 9:10 AM", 18, 81],
  ["James Whitfield", "normal", "independent", "45 Sycamore Ln", "", 70, 98, 16, 98.4, "120/76", "Walking", "Normal", 1, "Today 9:05 AM", 12, 69],
  ["Nancy Okoye", "normal", "group_home", "Cedar Ridge Home", "Rm 4", 66, 98, 14, 98.5, "118/70", "Light activity", "Normal", 2, "Today 8:50 AM", 15, 78],
];

const HOME_IDS = {};
let homeCounter = 0;
function homeId(home) {
  if (!HOME_IDS[home]) {
    homeCounter += 1;
    HOME_IDS[home] = `h${homeCounter}`;
  }
  return HOME_IDS[home];
}

const CONTACTS = [
  [{ name: "Sarah Alvarez", role: "Daughter", phone: "(303) 555-0142" }, { name: "Dr. Lin Chen", role: "Primary care", phone: "(303) 555-0188" }],
  [{ name: "Ray Webb", role: "Son", phone: "(720) 555-0110" }, { name: "Northside Clinic", role: "Care team", phone: "(720) 555-0199" }],
];

// GMA-1621 (operational map): Columbus-area coordinates per demo location.
// Group homes mirror docs/design-mockups/compass-data.js; independent-living
// addresses get fixed nearby coordinates. Residents of the same home share the
// home's coordinates (one building) — the map aggregates them into a home pin.
const LOCATION_COORDS = Object.freeze({
  "Maplewood House": { lat: 39.998, lng: -83.015 },
  "Cedar Ridge Home": { lat: 40.021, lng: -82.958 },
  "Harbor View Residence": { lat: 39.951, lng: -83.062 },
  "Birchwood Commons": { lat: 39.912, lng: -82.985 },
  "Elm Street House": { lat: 39.974, lng: -82.921 },
  "Northgate Home": { lat: 40.043, lng: -83.001 },
  "Riverside Apartments": { lat: 39.962, lng: -83.005 },
  "2214 Kenwood Ave": { lat: 40.006, lng: -82.94 },
  "388 Old Mill Ct": { lat: 39.93, lng: -83.03 },
  "45 Sycamore Ln": { lat: 40.028, lng: -83.055 },
  "77 Riverbend Dr": { lat: 39.944, lng: -82.955 },
  "901 Tamarack Way": { lat: 40.052, lng: -82.938 },
  "1740 Piedmont St": { lat: 39.902, lng: -83.052 },
});

export function getDemoClients(now = Date.now()) {
  return RAW.map(([name, status, livingEnv, home, room, hr, spo2, rr, temp, bp, activity, badge, devices, lastContact, agoMin, age], i) => {
    const off = status === "offline" || hr === 0;
    const [sys, dia] = typeof bp === "string" && bp.includes("/") ? bp.split("/").map((n) => parseInt(n, 10)) : [null, null];
    const seed = i * 7 + 3;
    const rising = status === "critical" || status === "atRisk";
    return {
      id: i + 1,
      name,
      // Vitals triage (vitalsFilterModel / VitalsPage) keys off `clientName`;
      // the client roster (ClientsPage) keys off `name`. Provide both so the
      // same demo record drives every screen without an "Unknown client".
      clientName: name,
      age,
      status,
      livingEnv,
      home,
      homeId: livingEnv === "group_home" ? homeId(home) : null,
      room,
      // Operational-map position (GMA-1621). Null-safe: an unmapped location
      // yields lat/lng of null and the map model drops the pin, never throws.
      lat: (LOCATION_COORDS[home] || {}).lat ?? null,
      lng: (LOCATION_COORDS[home] || {}).lng ?? null,
      online: !off,
      deviceCount: devices,
      lastContact,
      lastUpdate: now - agoMin * 60000,
      activity,
      riskReason: badge,
      vitals: off
        ? { hr: 0, spo2: 0, rr: 0, temp: 0, bp: { systolic: null, diastolic: null } }
        : { hr, spo2, rr, temp, bp: { systolic: sys, diastolic: dia } },
      baselines: { hr: Math.max(58, hr - (rising ? 26 : 4)) || 68, spo2: 98, rr: 16, temp: 98.4 },
      trends: off ? {} : { hr: trend(seed, hr, rising) },
      contacts: CONTACTS[i] || [{ name: "Emergency contact", role: "Family", phone: "(303) 555-0100" }],
    };
  });
}

export function getDemoAlerts(now = Date.now()) {
  const clients = getDemoClients(now);
  const TYPE = { critical: "Critical", atRisk: "At risk", caution: "Caution" };
  return clients
    .filter((c) => c.status === "critical" || c.status === "atRisk" || c.status === "caution")
    .map((c, i) => ({
      id: `a${i + 1}`,
      severity: c.status,
      type: c.riskReason || TYPE[c.status],
      clientName: c.name,
      location: c.room ? `${c.home} · ${c.room}` : c.home,
      createdAt: now - (i + 1) * 4 * 60000,
    }));
}

export function getDemoDevices(clients = getDemoClients()) {
  // online/offline flags for the Overview KPI (devices online/offline).
  // Accepts the caller's already-built client list so the Overview route can
  // derive devices from the same dataset + captured `now` (no extra clock read
  // or duplicate dataset regeneration).
  return clients.flatMap((c) =>
    Array.from({ length: c.deviceCount || 0 }, () => ({ online: c.online })),
  );
}

/**
 * GMA-1627 — demo dataset for the Messages screen (threads, broadcast
 * cohorts, family gating). Shapes match what MessagesPage consumes and
 * exercise the REAL messagesModel paths: alert-linked + home + client +
 * family threads, caregiver cohorts resolved via resolveBroadcastRecipients,
 * and a revoked-consent client (James Whitfield) so the family-gating guard
 * (blocked composer + blocked broadcast recipients) is visibly exercised.
 */
export function getDemoMessages(now = Date.now()) {
  const threads = [
    {
      id: "t-marcus",
      kind: "alert",
      name: "Marcus Webb — Fall response",
      tag: "Alert-linked · A-4471",
      severity: "critical",
      clientId: "c-marcus",
      familyVisible: true,
      preview: "Luis: 3 min out, taking the stairs",
      lastMessageAt: now - 1 * 60000,
      unread: 2,
      sub: "Critical fall · care team + family",
      channel: "Care team",
      alertLabel: "A-4471 Critical",
      participants: [
        { name: "Luis Ortega", role: "Primary caregiver" },
        { name: "Denise Webb", role: "Mother" },
        { name: "Dispatch", role: "Dispatcher" },
      ],
      context: {
        title: "Linked Alert · A-4471",
        rows: [
          { k: "Type", v: "Fall Detected" },
          { k: "Status", v: "Dispatched" },
          { k: "Latest HR", v: "112 bpm" },
          { k: "Responder", v: "Luis · ETA 3m" },
        ],
      },
      messages: [
        {
          kind: "system",
          text: "Fall detected — thread opened automatically from alert A-4471",
          at: now - 5 * 60000,
        },
        {
          kind: "message",
          sender: "NORA",
          role: "Companion AI",
          text: "Automated check-in sent to Marcus ×2 — no response. Line held open on Apple Watch speaker.",
          at: now - 4 * 60000,
          receipt: "read",
        },
        {
          kind: "message",
          sender: "S. Mitchell",
          role: "Dispatcher",
          text: "Luis, fall alert for Marcus — can you respond? Denise notified.",
          at: now - 3 * 60000,
          receipt: "read",
          self: true,
        },
        {
          kind: "message",
          sender: "Luis Ortega",
          role: "Primary caregiver",
          text: "On my way — 3 min out, taking the stairs.",
          at: now - 1 * 60000,
          receipt: "delivered",
        },
      ],
    },
    {
      id: "t-maplewood",
      kind: "home",
      name: "Maplewood House",
      tag: "Group home · 9 residents",
      severity: "atRisk",
      clientId: "c-dorothy",
      familyVisible: false,
      preview: "T. Nguyen: O2 started, SpO₂ back to 93%",
      lastMessageAt: now - 28 * 60000,
      unread: 0,
      sub: "House staff + on-call nurse",
      channel: "House staff",
      participants: [
        { name: "T. Nguyen", role: "House manager" },
        { name: "On-call nurse", role: "Care team" },
      ],
      context: {
        title: "Maplewood House",
        rows: [
          { k: "Residents", v: "9" },
          { k: "Active", v: "1 alert" },
        ],
      },
      messages: [
        {
          kind: "system",
          text: "Low SpO₂ alert (A-4470) linked — Dorothy Alvarez, Rm 3",
          at: now - 31 * 60000,
        },
        {
          kind: "message",
          sender: "T. Nguyen",
          role: "House manager",
          text: "Starting Dorothy on the O2 concentrator per care plan.",
          at: now - 30 * 60000,
          receipt: "read",
        },
        {
          kind: "message",
          sender: "S. Mitchell",
          role: "Dispatcher",
          text: "Thanks — on-call nurse looped in.",
          at: now - 29 * 60000,
          receipt: "read",
          self: true,
        },
        {
          kind: "message",
          sender: "T. Nguyen",
          role: "House manager",
          text: "O2 started, SpO₂ back to 93%. Keeping her indoors.",
          at: now - 28 * 60000,
          receipt: "read",
        },
      ],
    },
    {
      id: "t-deshawn",
      kind: "client",
      name: "Deshawn Pruitt — Wellness",
      tag: "NORA companion flag",
      severity: "atRisk",
      clientId: "c-deshawn",
      familyVisible: false,
      preview: "NORA: concerning language flagged, human review",
      lastMessageAt: now - 38 * 60000,
      unread: 1,
      sub: "Care team · crisis-safety review",
      channel: "Care team",
      participants: [
        { name: "NORA", role: "Companion AI" },
        { name: "A. Romero", role: "Caregiver" },
      ],
      context: {
        title: "Deshawn Pruitt · CC-041",
        rows: [
          { k: "Flag", v: "Companion language" },
          { k: "Check-ins", v: "1 missed" },
        ],
      },
      messages: [
        {
          kind: "system",
          text: "NORA companion flagged concerning language — crisis-safety escalation raised for human review",
          at: now - 40 * 60000,
        },
        {
          kind: "message",
          sender: "NORA",
          role: "Companion AI",
          text: "During a 2:10 PM chat Deshawn expressed hopelessness. A human should reach out now — this is context, not a diagnosis.",
          at: now - 39 * 60000,
          receipt: "read",
        },
        {
          kind: "message",
          sender: "A. Romero",
          role: "Caregiver",
          text: "On it — heading over for an in-person wellness check.",
          at: now - 38 * 60000,
          receipt: "sent",
        },
      ],
    },
    {
      id: "t-vasquez",
      kind: "family",
      name: "Vasquez Family",
      tag: "Family · Elena Vasquez",
      severity: "normal",
      clientId: "c-elena",
      familyVisible: true,
      preview: "You: she’s doing well, hydration reminders sent",
      lastMessageAt: now - 178 * 60000,
      unread: 0,
      sub: "Family app · permission-scoped",
      channel: "Family app",
      participants: [{ name: "R. Vasquez", role: "Sister" }],
      context: {
        title: "Vasquez Family",
        rows: [
          { k: "Client", v: "Elena Vasquez" },
          { k: "Access", v: "Wellness only" },
          { k: "Consent", v: "On file" },
        ],
      },
      messages: [
        {
          kind: "message",
          sender: "R. Vasquez",
          role: "Sister",
          text: "Saw the heat advisory — is Elena okay in this weather?",
          at: now - 182 * 60000,
          receipt: "read",
        },
        {
          kind: "message",
          sender: "S. Mitchell",
          role: "Dispatcher",
          text: "She’s doing well. We’ve sent hydration reminders and her caregiver is checking in.",
          at: now - 178 * 60000,
          receipt: "read",
          self: true,
        },
      ],
    },
    {
      // Consent revoked — exercises the visible family-gating guard: the
      // composer blocks sends (role=alert) until visibility is restored.
      id: "t-whitfield",
      kind: "family",
      name: "Whitfield Family",
      tag: "Family · James Whitfield",
      severity: "caution",
      clientId: "c-james",
      familyVisible: false,
      preview: "Consent renewal pending — sends blocked",
      lastMessageAt: now - 26 * 3600000,
      unread: 0,
      sub: "Family app · permission-scoped",
      channel: "Family app",
      participants: [{ name: "P. Whitfield", role: "Son" }],
      context: {
        title: "Whitfield Family",
        rows: [
          { k: "Client", v: "James Whitfield" },
          { k: "Access", v: "Suspended" },
          { k: "Consent", v: "Renewal pending" },
        ],
      },
      messages: [
        {
          kind: "system",
          text: "Family visibility suspended — annual consent renewal pending",
          at: now - 26 * 3600000,
        },
      ],
    },
  ];

  const caregivers = [
    { id: "cg-luis", name: "Luis Ortega", clientIds: ["c-marcus"] },
    { id: "cg-amara", name: "A. Romero", clientIds: ["c-elena", "c-deshawn"] },
    { id: "cg-tara", name: "T. Nguyen", clientIds: ["c-dorothy"] },
    { id: "cg-park", name: "J. Park", clientIds: ["c-james"] },
  ];

  const familyContacts = [
    { id: "fam-vasquez", name: "R. Vasquez", clientIds: ["c-elena"] },
    { id: "fam-webb", name: "Denise Webb", clientIds: ["c-marcus"] },
    { id: "fam-whitfield", name: "P. Whitfield", clientIds: ["c-james"] },
  ];

  // Per-client family visibility (consent) — c-james is revoked, so the
  // Whitfield contact is blocked out of family broadcasts (fail closed).
  const familyVisibility = {
    "c-marcus": true,
    "c-elena": true,
    "c-james": false,
  };

  const cohorts = [
    {
      id: "heat",
      label: "Caregivers — heat-impacted clients",
      kind: "caregiver",
      clientIds: ["c-marcus", "c-elena"],
    },
    {
      id: "homes",
      label: "All house managers",
      kind: "caregiver",
      clientIds: ["c-dorothy", "c-james"],
    },
    {
      id: "family-heat",
      label: "Families — heat-impacted (opt-in)",
      kind: "family",
      clientIds: ["c-marcus", "c-elena", "c-james"],
    },
  ];

  const recentBroadcasts = [
    {
      text: "Heat advisory — hydration prompts every 2h",
      to: "All house managers (8)",
      at: now - 190 * 60000,
    },
    {
      text: "I-70 E closure — reroute via Broad St",
      to: "East corridor staff (2 homes)",
      at: now - 72 * 60000,
    },
    {
      text: "Fireworks 9:30 PM — calming plans ready",
      to: "Elopement-risk homes (3)",
      at: now - 45 * 60000,
    },
  ];

  return { threads, caregivers, familyContacts, familyVisibility, cohorts, recentBroadcasts };
}

// GMA-1626 — Reports hub fixtures: a generated Daily SITRep preview (findings
// carry priority/source/deviceOffline so the page exercises the REAL
// noraSitrepModel status + confidence paths), the scheduled-report rail, and
// the export history. Prose is deliberately jargon-free so the same fixture
// renders for both audiences (the family jargon guard is pinned in tests).
export function getDemoReports(now = Date.now()) {
  return {
    sitrep: {
      generatedAt: now - 8 * 60000,
      bluf:
        "104 clients monitored over the past 24 hours. 1 critical incident (Marcus Webb — fall, response underway), 8 clients on active watch, 1 site on backup power (Birchwood Commons). No client is currently without coverage.",
      findings: [
        {
          title: "Critical fall — Marcus Webb",
          detail:
            "Fall detected 2:45 PM; no check-in response. Caregiver en route, dispatch engaged.",
          priority: "critical",
          source: "telemetry",
          delta: "NEW · CRITICAL",
        },
        {
          title: "Heat advisory driving watch volume",
          detail:
            "8 clients flagged for hydration; heat index crossed 95°F at 11:00 AM. Hydration prompts every 2h in effect.",
          priority: "watch",
          source: "pattern",
          delta: "+6 watches vs usual",
        },
        {
          title: "Birchwood gateway offline",
          detail:
            "8 residents on cellular backup since 12:41 PM. Higher emergency-detection latency; manual staff checks active.",
          priority: "watch",
          source: "telemetry",
          deviceOffline: true,
          delta: "MODERATE impact",
        },
        {
          title: "Response time improving",
          detail:
            "Average alert response 2m 14s today vs 2m 43s yesterday — the night-shift change is holding.",
          priority: "info",
          source: "logs",
          delta: "−18% vs yesterday",
        },
      ],
      followups: [
        {
          tag: "NOW",
          text: "Confirm Marcus Webb responder arrival and update family once assessed.",
        },
        {
          tag: "THIS SHIFT",
          text: "Verify Birchwood cellular backup hourly until the gateway is restored.",
        },
        {
          tag: "24–72H",
          text: "Review heat-advisory hydration adherence; extend the safety-watch profile if the advisory continues.",
        },
      ],
    },
    scheduled: [
      { name: "Daily SITRep — Agency", cadence: "Daily 6:00 AM", recipients: "Ops + on-call leads", active: true },
      { name: "Weekly Vitals Trend — At-risk", cadence: "Mon 7:00 AM", recipients: "Clinical team", active: true },
      { name: "Family Weekly — All clients", cadence: "Sun 5:00 PM", recipients: "Family app + email", active: true },
      { name: "Monthly Care-Plan Review", cadence: "1st of month", recipients: "Care coordinators", active: false },
    ],
    exportHistory: [
      { name: "Incident SITRep — Marcus Webb", detail: "PDF · attached to case FA-2026-0706-001", at: now - 10 * 60000, actor: "NORA" },
      { name: "Family Update — Dorothy Alvarez", detail: "Sent to family app · SpO₂ recovery note", at: now - 36 * 60000, actor: "S. Mitchell" },
      { name: "Group Home Summary — Maplewood", detail: "PDF export · 9 residents", at: now - 110 * 60000, actor: "T. Nguyen" },
      { name: "Device Health Report", detail: "CSV export · full fleet", at: now - 6 * 3600000, actor: "J. Park" },
    ],
  };
}

// GMA-1629 — Audit Log + Settings demo fixtures.
// Staff actors for the demo audit trail (the clients are the TARGETS).
const AUDIT_STAFF = [
  "A. Brennan (RN)",
  "M. Osei (Care Lead)",
  "T. Delgado (Dispatcher)",
  "Nora (AI)",
];

/**
 * Deterministic audit trail derived from the 16 demo clients: escalated
 * clients produce alert/contact events, offline clients produce contact
 * attempts, plus a few agency-level entries (no client target). Shapes match
 * the auditSettingsModel entry surface: { id, actor, clientId, clientName,
 * eventType, severity, timestamp } — nothing beyond it (no PHI).
 */
export function getDemoAuditTrail(now = Date.now()) {
  const clients = getDemoClients(now);
  const events = [];
  const push = (actor, client, eventType, severity) => {
    const n = events.length + 1;
    events.push({
      id: `ev${n}`,
      actor,
      clientId: client ? client.id : null,
      clientName: client ? client.name : null,
      eventType,
      severity,
      // Newest-first spacing so relative times read naturally in the table.
      timestamp: now - n * 7 * 60000,
    });
  };
  clients.forEach((c, i) => {
    const actor = AUDIT_STAFF[i % AUDIT_STAFF.length];
    if (c.status === "critical") {
      push(actor, c, "alert_lifecycle", "critical");
      push(actor, c, "contact_attempt", "atRisk");
    } else if (c.status === "atRisk") {
      push(actor, c, "alert_lifecycle", "atRisk");
    } else if (c.status === "offline") {
      push(actor, c, "contact_attempt", "caution");
    } else if (c.status === "caution" && i % 2 === 0) {
      push(actor, c, "sitrep", "caution");
    } else if (i % 3 === 0) {
      push(actor, c, "access_instruction_view", "normal");
    }
  });
  // Agency-level events (no client target) round out the taxonomy.
  push("Ops Console", null, "broadcast_send", "normal");
  push("A. Brennan (RN)", null, "report_export", "normal");
  push("M. Osei (Care Lead)", null, "config_change", "caution");
  push("Ops Console", null, "permission_change", "normal");
  return events;
}

/**
 * Alert-threshold settings fixtures ({ key, label, unit, agencyDefault,
 * clientValue }). clientValue !== default marks a demo override so the
 * Settings screen shows both the "Overridden" and default states.
 */
/**
 * GMA-1742 — demo traffic corridor (REACT_APP_DEMO_DATA=1 only): the I-70 E
 * lane-closure scenario the demo INTEL rail already tells, drawn along the
 * demo metro (Columbus) so the overlay is visible without waiting for a
 * live degraded-access event. Id is demo-prefixed — disjoint from the
 * routes-corridor-* live ids.
 */
export function getDemoCorridors() {
  return [
    {
      id: "demo-corridor-i70e",
      severity: "caution",
      label: "Traffic & Responder Access",
      paths: [
        [
          [-83.08, 39.945],
          [-83.03, 39.952],
          [-82.98, 39.96],
          [-82.93, 39.968],
          [-82.88, 39.975],
        ],
      ],
      card: {
        id: "demo-corridor-i70e",
        title: "Traffic & Responder Access",
        subtitle: "Traffic + routes feed",
        severity: "caution",
        severityChip: "ELEVATED",
        summary:
          "I-70 E lane closure adds +18 min to 2 homes in the east corridor.",
        lead: "\u2014",
        window: "Until cleared",
        sourceTag: "Traffic + routes feed",
        clientIds: [],
        actions: [],
      },
    },
  ];
}

export function getDemoAlertThresholds() {
  return [
    { key: "hr_high", label: "High heart rate", unit: "bpm", agencyDefault: 110, clientValue: 120 },
    { key: "hr_low", label: "Low heart rate", unit: "bpm", agencyDefault: 50, clientValue: null },
    { key: "spo2_low", label: "Low SpO₂", unit: "%", agencyDefault: 92, clientValue: 90 },
    { key: "temp_high", label: "High temperature", unit: "°F", agencyDefault: 100.4, clientValue: null },
    { key: "no_movement", label: "No-movement window", unit: "min", agencyDefault: 30, clientValue: null },
    { key: "missed_checkin", label: "Missed check-in grace", unit: "min", agencyDefault: 45, clientValue: 60 },
  ];
}

// Device archetypes for the Devices screen (GMA-1623). Order matters: a
// client's Nth device is DEVICE_KINDS[N % 3], so 1-device clients get a
// wearable, 2-device clients add a Pontosense radar, and 3-device clients
// also get a gateway hub the radar routes through (exercising the model's
// offline-gateway cascade). Capabilities come from the devicesModel set.
const DEVICE_KINDS = [
  {
    type: "Apple Watch",
    battery: true,
    capabilities: ["communication", "fall_detection", "vitals", "location"],
  },
  {
    type: "Pontosense radar",
    battery: false, // mains powered — battery not applicable
    capabilities: ["fall_detection", "vitals", "environmental"],
  },
  {
    type: "Gateway hub",
    battery: false,
    capabilities: ["gateway"],
  },
];

export function getDemoDeviceFleet(now = Date.now(), clients = getDemoClients(now)) {
  // Full device rows for the Devices screen (getDemoDevices above stays the
  // Overview KPI's lightweight online/offline shape). Deterministic: derived
  // only from the client dataset + `now`, so every render agrees.
  return clients.flatMap((c) => {
    const count = c.deviceCount || 0;
    const hasHub = count >= 3;
    return Array.from({ length: count }, (_, i) => {
      const kind = DEVICE_KINDS[i % DEVICE_KINDS.length];
      const offline = !c.online;
      return {
        id: `dev-${c.id}-${i + 1}`,
        name: `${kind.type} — ${c.name}`,
        type: kind.type,
        clientId: c.id,
        clientName: c.name,
        status: offline ? "offline" : "online",
        battery: kind.battery ? (offline ? 8 : 35 + ((c.id * 13 + i * 29) % 60)) : null,
        signal: offline ? 0 : 45 + ((c.id * 7 + i * 17) % 50),
        firmwarePending: !offline && (c.id + i) % 9 === 0,
        capabilities: kind.capabilities,
        // The radar reaches the network through the client's hub when one
        // exists (the devicesModel gateway-cascade case).
        gatewayId: hasHub && kind.type === "Pontosense radar" ? `dev-${c.id}-3` : undefined,
        lastSeenAt: (c.lastUpdate || now) - i * 3 * 60000,
      };
    });
  });
}

// Whether demo mode is active (commandcenter build sets REACT_APP_DEMO_DATA=1).
export const DEMO_DATA_ENABLED = process.env.REACT_APP_DEMO_DATA === "1";

/* ------------------------------------------------------------------------ *
 * GMA-1628 — demo dataset for the Schedule / Activities screen.
 *
 * Appended at the END of this module (below DEMO_DATA_ENABLED) so the
 * GMA-1626 / GMA-1627 demo appends (#333 / #334) and this one merge with
 * trivial additive conflicts.
 *
 * Shapes match what SchedulePage consumes and exercise the REAL
 * scheduleModel paths: per-client day buckets whose activity statuses DERIVE
 * from startMs vs now (completed / missed / needs_follow_up / upcoming — a
 * client-declared "not done" maps to needs_follow_up, never missed), and a
 * recurring medication series whose past completed occurrences are locked by
 * planSeriesEdit (GMA-1611) while tomorrow's stays editable. The client
 * selector reuses the same demo roster as every other screen
 * (getDemoClients), so names/homes/severities agree across the app.
 * ------------------------------------------------------------------------ */
export function getDemoSchedule(now = Date.now()) {
  const H = 3600000;
  // Selector roster: the shared demo clients, in a per-client calendar shape.
  // The mockup's timezone chip is ET — the demo org is Eastern-based.
  const clients = getDemoClients(now).map((c) => ({
    id: `c${c.id}`,
    name: c.name,
    home: c.home,
    room: c.room,
    status: c.status,
    timezone: "America/New_York",
  }));

  // Recurring morning-medication series for Dorothy (c1): yesterday + today
  // completed (GMA-1611 locked), tomorrow still editable.
  const medSeries = [
    { id: "med-c1-yday", startMs: now - 30 * H, completed: true },
    { id: "med-c1-today", startMs: now - 6 * H, completed: true },
    { id: "med-c1-tmrw", startMs: now + 18 * H, completed: false },
  ];

  const days = [
    {
      id: "yesterday",
      label: "Yesterday",
      activities: [
        {
          id: "med-c1-yday",
          clientId: "c1",
          type: "medication",
          title: "Morning medication",
          timeLabel: "8:00 AM",
          startMs: now - 30 * H,
          completed: true,
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
          notes: "Take with breakfast — full glass of water.",
          occurrences: medSeries,
        },
        {
          id: "hydration-c1-yday",
          clientId: "c1",
          type: "hydration",
          title: "Hydration reminder",
          timeLabel: "11:30 AM",
          startMs: now - 26 * H,
          completed: false, // past + untouched -> missed
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
        },
      ],
    },
    {
      id: "today",
      label: "Today",
      activities: [
        {
          id: "med-c1-today",
          clientId: "c1",
          type: "medication",
          title: "Morning medication",
          timeLabel: "8:00 AM",
          startMs: now - 6 * H,
          completed: true, // past + completed -> GMA-1611 locked in the drawer
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
          notes: "Take with breakfast — full glass of water.",
          prefs: "Prefers pills with applesauce.",
          occurrences: medSeries,
        },
        {
          id: "meal-c1-today",
          clientId: "c1",
          type: "meal",
          title: "Breakfast",
          timeLabel: "8:30 AM",
          startMs: now - 5.5 * H,
          completed: true,
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
        },
        {
          id: "dayprog-c1-today",
          clientId: "c1",
          type: "day_program",
          title: "Day program transport",
          timeLabel: "9:15 AM",
          startMs: now - 5 * H,
          needsFollowUp: true, // client declared not done -> follow-up (yellow), NOT missed
          caregiver: "T. Nguyen",
          recurrenceLabel: "Weekdays",
          notes: "Van pickup at the front entrance.",
        },
        {
          id: "hydration-c1-today",
          clientId: "c1",
          type: "hydration",
          title: "Hydration reminder",
          timeLabel: "11:30 AM",
          startMs: now - 2 * H,
          completed: false, // past + untouched -> missed (RED reserved for this)
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
        },
        {
          id: "wellness-c1-today",
          clientId: "c1",
          type: "wellness_check",
          title: "Afternoon wellness check",
          timeLabel: "1:00 PM",
          startMs: now + 1 * H,
          completed: false, // future -> upcoming + editable
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
          notes: "Check hydration and afternoon meds readiness.",
          occurrences: [
            { id: "wellness-c1-today", startMs: now + 1 * H, completed: false },
            { id: "wellness-c1-tmrw", startMs: now + 25 * H, completed: false },
          ],
        },
        {
          id: "quiet-c1-today",
          clientId: "c1",
          type: "quiet_time",
          title: "Quiet time",
          timeLabel: "2:00 PM",
          startMs: now + 2 * H,
          completed: false,
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
        },
        {
          id: "evening-c1-today",
          clientId: "c1",
          type: "evening_routine",
          title: "Evening routine",
          timeLabel: "7:30 PM",
          startMs: now + 6 * H,
          completed: false,
          caregiver: "Night staff",
          recurrenceLabel: "Daily",
        },
        {
          id: "sleep-c1-today",
          clientId: "c1",
          type: "sleep_routine",
          title: "Sleep routine",
          timeLabel: "10:00 PM",
          startMs: now + 9 * H,
          completed: false,
          caregiver: "Night staff",
          recurrenceLabel: "Daily",
          prefs: "White noise helps her settle.",
        },
        // Marcus Webb (c2) — proves the client selector actually filters.
        {
          id: "med-c2-today",
          clientId: "c2",
          type: "medication",
          title: "Morning medication",
          timeLabel: "9:00 AM",
          startMs: now - 5 * H,
          completed: false, // missed — consistent with his active fall alert
          caregiver: "Luis Ortega",
          recurrenceLabel: "Daily",
        },
        {
          id: "wellness-c2-today",
          clientId: "c2",
          type: "wellness_check",
          title: "Post-fall wellness check",
          timeLabel: "2:00 PM",
          startMs: now + 2 * H,
          completed: false,
          caregiver: "Luis Ortega",
          notes: "Follow up on this morning's fall alert (A-4471).",
        },
      ],
    },
    {
      id: "tomorrow",
      label: "Tomorrow",
      activities: [
        {
          id: "med-c1-tmrw",
          clientId: "c1",
          type: "medication",
          title: "Morning medication",
          timeLabel: "8:00 AM",
          startMs: now + 18 * H,
          completed: false, // future occurrence of the series — still editable
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
          occurrences: medSeries,
        },
        {
          id: "wellness-c1-tmrw",
          clientId: "c1",
          type: "wellness_check",
          title: "Afternoon wellness check",
          timeLabel: "1:00 PM",
          startMs: now + 25 * H,
          completed: false,
          caregiver: "S. Mitchell",
          recurrenceLabel: "Daily",
        },
      ],
    },
  ];

  return { clients, days, defaultDayId: "today" };
}
/* ------------------------------- end GMA-1628 --------------------------- */

/* ------------------------------ GMA-1625 INTEL --------------------------- */

/**
 * Active watches for the INTEL screen, mirroring the hi-fi mockup
 * (CompassCare INTEL.dc.html). Impact resolves GEOGRAPHICALLY via
 * intelModel.clientsInWatchZone against the demo roster's coordinates —
 * except cohort watches (explicit clientIds), e.g. the NORA companion watch.
 * Severity chips use the mockup vocabulary (CRITICAL/HIGH/ELEVATED) tinted by
 * the shared severity tokens (critical/atRisk/caution).
 */
export function getDemoWatches() {
  return [
    {
      id: "w-nora-checkins",
      icon: "Forum",
      title: "NORA Companion & Check-ins",
      subtitle: "Companion Intelligence · NORA",
      severity: "critical",
      severityChip: "CRITICAL",
      summary: "Companion flagged concerning language + 1 client overdue on check-ins.",
      lead: "Real-time lead",
      window: "Active now",
      sourceTag: "NORA companion",
      clientIds: [1, 2, 5, 9],
      detection: {
        chips: ["NORA companion", "Check-in scheduler"],
        text: "NORA's companion conversations flagged concerning language for review, and the check-in scheduler shows one client past their confirmation window.",
      },
      triggers: [
        { term: "Concerning language", text: "may signal distress, confusion, or an unmet need — human review is queued." },
        { term: "Missed check-ins", text: "break the reassurance loop and can hide an emergent situation." },
      ],
      actions: [
        { when: "NOW", text: "Review the flagged companion transcript excerpts and call Dorothy's caregiver." },
        { when: "THIS SHIFT", text: "Re-run the overdue check-in and confirm reachability for all 4 clients." },
      ],
      posture: { level: "Base L4 from Critical severity", confidence: "High confidence", text: "NORA is holding the companion line open and escalating unreviewed flags every 15 minutes." },
    },
    {
      id: "w-heat-hydration",
      icon: "WbSunny",
      typeKey: "heat",
      title: "Heat & Hydration",
      subtitle: "Environmental Stressor · Weather",
      severity: "atRisk",
      severityChip: "HIGH",
      summary: "Heat index 101°F until 8 PM — dehydration & agitation risk climbing.",
      lead: "5h lead",
      window: "Until 8:00 PM",
      sourceTag: "NWS + HealthKit",
      zone: { center: { lat: 39.97, lng: -82.99 }, radiusKm: 9 },
      detection: {
        chips: ["NWS heat feed", "Hydration logs", "HealthKit HR/HRV"],
        text: "Heat index crossed the 95°F client-safety threshold at 11:00 AM and is trending up. NORA cross-referenced the forecast against each client's hydration logging and indoor temp — impacted clients are behind on fluids or in warm units.",
      },
      triggers: [
        { term: "Dehydration", text: "agitation, confusion, and higher fall risk, especially with TBI and dementia." },
        { term: "Heat", text: "lowers the seizure threshold for seizure-risk clients and stresses cardiac/COPD clients." },
        { term: "Warm units", text: "disrupt sleep tonight, which feeds next-day mood and behavior." },
      ],
      actions: [
        { when: "NOW", text: "Send hydration reminders to all impacted clients; ask caregivers to confirm fluids in person for Sam and Elena." },
        { when: "THIS SHIFT", text: "Verify AC/cooling at warm units (Birchwood on backup, Harbor View Rm 6). Move activities to cooler spaces." },
        { when: "TONIGHT", text: "Shift heat-sensitive clients to the Safety-Watch profile so overnight thresholds tighten." },
      ],
      posture: { level: "Base L3 from High severity", confidence: "High confidence", text: "Separate from risk — how actively NORA works this watch. Hydration nudges auto-send; abnormal HR/HRV drift pages the shift lead." },
    },
    {
      id: "w-behavioral-mood",
      icon: "Mood",
      typeKey: "health_advisory",
      title: "Behavioral & Mood State",
      subtitle: "Wellness Trend · HealthKit",
      severity: "atRisk",
      severityChip: "HIGH",
      summary: "HealthKit signals show mood/agitation risk rising for 5 clients.",
      lead: "1–2 days lead",
      window: "Rolling 48h",
      sourceTag: "HealthKit trend model",
      clientIds: [3, 5, 8, 11, 13],
      detection: {
        chips: ["HealthKit HRV", "Sleep stages", "Activity rhythm"],
        text: "The trend model correlates falling HRV, fragmented sleep, and reduced daytime activity — five clients drifted past their personal baselines over the last 48 hours.",
      },
      triggers: [
        { term: "Mood decline", text: "precedes behavioral incidents and med-adherence slips by 1–2 days." },
        { term: "Sleep fragmentation", text: "amplifies next-day agitation, wandering, and fall risk." },
      ],
      actions: [
        { when: "NOW", text: "Flag the five clients on the shift board; brief caregivers on de-escalation preferences." },
        { when: "THIS SHIFT", text: "Schedule calming activities and confirm evening med administration in person." },
      ],
      posture: { level: "Base L2 from High severity", confidence: "Moderate confidence", text: "NORA tightens companion check-in tone analysis and watches overnight sleep onset for the cohort." },
    },
    {
      id: "w-gait-fall",
      icon: "DirectionsWalk",
      typeKey: "health_advisory",
      title: "Gait & Fall-Precursor",
      subtitle: "Mobility Trend · Watch + Radar",
      severity: "atRisk",
      severityChip: "HIGH",
      summary: "Declining gait metrics flag 4 clients at rising fall risk — before a fall.",
      lead: "1–3 weeks lead",
      window: "Rolling 21d",
      sourceTag: "Watch gait + radar",
      clientIds: [1, 4, 6, 15],
      detection: {
        chips: ["Apple Watch gait", "Pontosense radar", "Baseline drift"],
        text: "Walking speed, step length, and double-support time are drifting below personal baselines for four clients — the pattern that precedes falls by one to three weeks.",
      },
      triggers: [
        { term: "Gait decline", text: "is the strongest leading indicator of a fall — intervening now prevents the incident." },
        { term: "Night transfers", text: "bed-to-bathroom trips are where declining gait becomes a 2 AM fall." },
      ],
      actions: [
        { when: "NOW", text: "Order PT screens for the four flagged clients; check footwear and walker condition." },
        { when: "THIS SHIFT", text: "Clear pathway clutter and verify night-light coverage on bed-to-bathroom routes." },
      ],
      posture: { level: "Base L2 from High severity", confidence: "High confidence", text: "Radar fall-detection sensitivity is raised for the cohort; NORA reviews overnight movement each morning." },
    },
    {
      id: "w-wandering-elopement",
      icon: "DirectionsRun",
      typeKey: "fireworks",
      title: "Wandering & Elopement Risk",
      subtitle: "Community Event · Pattern",
      severity: "atRisk",
      severityChip: "HIGH",
      summary: "Sundown + fireworks tonight raise elopement risk for 3 clients.",
      lead: "4h lead",
      window: "6 PM – 1 AM",
      sourceTag: "Pattern + events feed",
      clientIds: [3, 7, 12],
      detection: {
        chips: ["Elopement history", "Events feed", "Sundowning pattern"],
        text: "A permitted fireworks display overlaps the sundowning window for three clients with elopement history — noise and light events triple exit-seeking behavior.",
      },
      triggers: [
        { term: "Sensory distress", text: "fireworks noise drives exit-seeking and agitation in sensory-sensitive clients." },
        { term: "Elopement", text: "an unnoticed exit after dark is the highest-consequence scenario tonight." },
        { term: "Sleep disruption", text: "late noise pushes bedtime and destabilizes tomorrow's routine." },
      ],
      actions: [
        { when: "NOW", text: "Confirm door-sensor arming and perimeter alerts at the three clients' residences." },
        { when: "THIS SHIFT", text: "Plan indoor evening activity away from street-facing rooms; prep noise-dampening." },
        { when: "TONIGHT", text: "Overnight staff double-check exits at 10 PM and after the display ends." },
      ],
      posture: { level: "Base L3 from High severity", confidence: "High confidence", text: "Perimeter-exit alerts page immediately (no batching) until 1 AM for the cohort." },
    },
    {
      id: "w-power-connectivity",
      icon: "PowerOff",
      typeKey: "power_outage",
      title: "Power & Connectivity",
      subtitle: "Utility · AEP",
      severity: "caution",
      severityChip: "ELEVATED",
      summary: "AEP outage at Birchwood — 8 residents on backup power & cellular.",
      lead: "— lead",
      window: "Restoration est. 4:30 PM",
      sourceTag: "AEP + device telemetry",
      zone: { center: { lat: 39.912, lng: -82.985 }, radiusKm: 2.5 },
      detection: {
        chips: ["AEP outage map", "Hub telemetry", "Cellular failover"],
        text: "The utility outage polygon covers Birchwood Commons. Hubs failed over to cellular backup — emergency detection latency is higher until mains power returns.",
      },
      triggers: [
        { term: "Device coverage loss", text: "backup cellular adds detection latency; battery devices drain faster." },
        { term: "Missed routines", text: "no lights/AC disrupts meals, meds, and afternoon routines." },
        { term: "Refrigerated meds", text: "fridge temps drift after ~4 hours without power." },
      ],
      actions: [
        { when: "NOW", text: "Manual staff check-ins at Birchwood every 30 minutes while on backup." },
        { when: "THIS SHIFT", text: "Verify hub batteries and move refrigerated meds to the powered unit if restoration slips." },
      ],
      posture: { level: "Base L2 from Elevated severity", confidence: "High confidence", text: "NORA watches hub heartbeats at 1-minute cadence and pages on any device dropout at the affected home." },
    },
    {
      id: "w-traffic-access",
      icon: "DirectionsBus",
      typeKey: "public_safety",
      title: "Traffic & Responder Access",
      subtitle: "Situational · Routes",
      severity: "caution",
      severityChip: "ELEVATED",
      summary: "I-70 E lane closure adds +18 min to 2 homes in the east corridor.",
      lead: "Until cleared",
      window: "CLOSURE +18 MIN",
      sourceTag: "Traffic + routes feed",
      zone: { center: { lat: 39.974, lng: -82.921 }, radiusKm: 6 },
      detection: {
        chips: ["Traffic feed", "Route model"],
        text: "An I-70 eastbound lane closure adds roughly 18 minutes to responder and caregiver routes reaching the two eastern homes.",
      },
      triggers: [
        { term: "Route access delay", text: "longer response times raise the stakes of any emergency at the affected homes." },
      ],
      actions: [
        { when: "NOW", text: "Notify on-call staff of the alternate route; stage the east-side caregiver closer if possible." },
      ],
      posture: { level: "Base L1 from Elevated severity", confidence: "Moderate confidence", text: "NORA re-checks the closure every 10 minutes and clears the watch when routes normalize." },
    },
  ];
}

/** Saved watch profiles (mockup): named threshold presets a team toggles. */
export function getDemoWatchProfiles() {
  return [
    { id: "p-heat-vulnerable", icon: "WbSunny", label: "Heat + Vulnerable cohort", on: true },
    { id: "p-overnight-elopement", icon: "DarkMode", label: "Overnight elopement", on: true },
    { id: "p-respiratory-aqi", icon: "Air", label: "Respiratory + AQI", on: false },
  ];
}
/* ----------------------------- end GMA-1625 ------------------------------ */

/* ----------------------------- GMA-1622 DISPATCH -------------------------- */

/**
 * Call trees for the Dispatch screen (mockup: Call Tree Library). Levels use
 * the dispatchModel shape (level/contactName/methods/escalationMs/retries) so
 * "Test Call Tree" runs the REAL simulateCallTree — never contacts anyone.
 * `simResponses` are the deterministic per-level outcomes the demo test run
 * uses, so every render of the simulation is identical.
 */
export function getDemoCallTrees() {
  return [
    {
      id: "t-standard",
      icon: "AccountTree",
      name: "Standard Escalation",
      scope: "Org default · all clients",
      desc: "Default escalation for any client without a house or client override. Primary caregiver first, then secondary, shift lead, and the dispatch desk.",
      override: false,
      overrideNote: null,
      levels: [
        { level: "L1", contactName: "Primary caregiver", role: "Assigned caregiver", methods: ["call", "text", "app"], escalationMs: 180000, retries: 2, note: "Critical alerts page L1 and L2 simultaneously." },
        { level: "L2", contactName: "Secondary caregiver", role: "Backup caregiver", methods: ["call", "text"], escalationMs: 180000, retries: 1 },
        { level: "L3", contactName: "On-call shift lead", role: "Care team", methods: ["call"], escalationMs: 300000, retries: 1 },
        { level: "L4", contactName: "Dispatch desk", role: "24/7 coordination", methods: ["call"], escalationMs: 0, retries: 1 },
      ],
      rules: [
        "Alert severity At-risk or higher starts the tree immediately.",
        "Critical alerts page L1 + L2 at the same time (no wait).",
        "Every attempt is audit-logged with outcome and timing.",
        "The tree keeps running until a human confirms client contact.",
      ],
      simResponses: { L1: "no_answer", L2: "answered" },
    },
    {
      id: "t-maplewood",
      icon: "HomeWork",
      name: "Maplewood House",
      scope: "Group-home override · 4 residents",
      desc: "House-staffed escalation for Maplewood residents — the overnight house lead answers before family contacts are paged.",
      override: true,
      overrideNote: "Overrides Standard Escalation for Maplewood House residents (house staffing model).",
      levels: [
        { level: "L1", contactName: "Angela Torres", role: "House lead (on shift)", methods: ["call", "app"], escalationMs: 120000, retries: 2 },
        { level: "L2", contactName: "Sarah Alvarez", role: "Family — Dorothy", methods: ["call", "text"], escalationMs: 180000, retries: 1 },
        { level: "L3", contactName: "Dr. Lin Chen", role: "Primary care", methods: ["call"], escalationMs: 300000, retries: 1 },
        { level: "L4", contactName: "Dispatch desk", role: "24/7 coordination", methods: ["call"], escalationMs: 0, retries: 1 },
      ],
      rules: [
        "House lead is always L1 while a shift is active.",
        "Family contacts are paged only after the house lead window lapses.",
        "Every attempt is audit-logged with outcome and timing.",
      ],
      simResponses: { L1: "answered" },
    },
    {
      id: "t-marcus",
      icon: "PersonPin",
      name: "Marcus Webb",
      scope: "Client override · fall-risk protocol",
      desc: "Fall-protocol escalation for Marcus — son first with a short window, then the care clinic, then dispatch.",
      override: true,
      overrideNote: "Overrides Standard Escalation for Marcus Webb (fall-risk protocol).",
      levels: [
        { level: "L1", contactName: "Ray Webb", role: "Son", methods: ["call", "text"], escalationMs: 120000, retries: 2 },
        { level: "L2", contactName: "Northside Clinic", role: "Care team", methods: ["call"], escalationMs: 240000, retries: 1 },
        { level: "L4", contactName: "Dispatch desk", role: "24/7 coordination", methods: ["call"], escalationMs: 0, retries: 1 },
      ],
      rules: [
        "Fall alerts skip L3 — clinic no-answer goes straight to dispatch.",
        "Every attempt is audit-logged with outcome and timing.",
      ],
      simResponses: { L1: "no_answer", L2: "no_answer", L4: "answered" },
    },
  ];
}

/** Contact Attempts Today (mockup audit rows) — deterministic demo feed. */
export function getDemoContactAttempts(now = Date.now()) {
  const t = (minAgo) => now - minAgo * 60000;
  return [
    { id: "ca1", action: "Call answered", at: t(9), target: "Ray Webb — L1 primary", detail: "Fall Detected · answered in 38s", who: "auto-escalation" },
    { id: "ca2", action: "Call placed", at: t(11), target: "Ray Webb — L1 primary", detail: "Fall Detected · attempt 1 of 2", who: "auto-escalation" },
    { id: "ca3", action: "Text sent", at: t(34), target: "Sarah Alvarez — L2 family", detail: "Low SpO₂ · fallback after L1 window", who: "auto-escalation" },
    { id: "ca4", action: "Call answered", at: t(52), target: "Angela Torres — L1 house lead", detail: "Check-in failed · answered in 21s", who: "auto-escalation" },
    { id: "ca5", action: "App ping", at: t(75), target: "Primary caregiver — L1", detail: "Door Open · acknowledged", who: "auto-escalation" },
  ];
}
/* ---------------------------- end GMA-1622 -------------------------------- */

/* ------------------------------ GMA-1624 ---------------------------------- */
// SITRep Board demo dataset: one Nora-style situation report per demo client,
// across all three cadences. The prose here is DEMO fixture text (the real
// SITRep prose is generated by Nora on the backend — the GMA-1624 follow-up);
// the STRUCTURE is the contract: developments carry priority + source +
// deviceOffline exactly as noraSitrepModel.resolveConfidence / overallStatus
// consume them, so the board exercises the REAL engine paths — device-offline
// clients roll up LOW confidence, fall/vitals events roll up AMBER/RED — and
// the family rendering stays inside the hasClinicianJargon guard.

const SITREP_CADENCE_META = Object.freeze({
  daily: Object.freeze({
    label: "Daily",
    mastTitle: "Daily Briefing",
    windowLabel: "Past 24 hours",
    deltaPhrase: "vs yesterday",
    periodWord: "day",
  }),
  weekly: Object.freeze({
    label: "Weekly",
    mastTitle: "Weekly Briefing",
    windowLabel: "Past 7 days",
    deltaPhrase: "vs 7-day average",
    periodWord: "week",
  }),
  monthly: Object.freeze({
    label: "Monthly",
    mastTitle: "Monthly Review",
    windowLabel: "Past 30 days",
    deltaPhrase: "vs 30-day average",
    periodWord: "month",
  }),
});

// riskReason badge → the development category it lands in, with its priority.
const SITREP_BADGE_SLOTS = Object.freeze({
  "Fall Detected": { category: "safety", priority: "critical" },
  "Perimeter Exit": { category: "safety", priority: "watch" },
  "No Movement": { category: "safety", priority: "watch" },
  "Door Open": { category: "safety", priority: "watch" },
  "Low SpO₂": { category: "vitals", priority: "critical" },
  "High HR": { category: "vitals", priority: "watch" },
  "High Temp": { category: "vitals", priority: "watch" },
  "Elevated BP": { category: "vitals", priority: "watch" },
  "Hydration Watch": { category: "vitals", priority: "watch" },
  "Missed Check-in": { category: "routine", priority: "watch" },
  "Low Activity": { category: "mood", priority: "watch" },
});

const SITREP_BADGE_TEXT = Object.freeze({
  "Fall Detected": "Fall detected; no check-in response. Responder dispatched and the escalation tree engaged.",
  "Perimeter Exit": "Perimeter exit detected while off-site; movement is being tracked and a caregiver was notified.",
  "No Movement": "No movement registered for an extended stretch; a wellness prompt was sent and is awaiting response.",
  "Door Open": "Exterior door open outside the usual pattern; staff on site confirmed the residence is secure.",
  "Low SpO₂": "Oxygen saturation below the safe threshold; escalation active and vitals rechecked on a tightened interval.",
  "High HR": "Heart rate elevated above the usual range alongside pacing behavior; monitoring interval tightened.",
  "High Temp": "Body temperature elevated above the usual range; hydration and comfort prompts delivered.",
  "Elevated BP": "Blood pressure reading above the usual range; a recheck is scheduled and the care team notified.",
  "Hydration Watch": "Fluid-intake logging is behind schedule during the heat advisory; extra reminders in effect.",
  "Missed Check-in": "A scheduled check-in was not acknowledged; a follow-up call is queued with the on-shift caregiver.",
  "Low Activity": "Activity level noticeably below the usual pattern; engagement prompts increased.",
});

const SITREP_FAMILY_WATCH_TEXT = Object.freeze({
  "Fall Detected": "A fall was picked up and responders were sent right away — the care team is with this and will keep you posted.",
  "Perimeter Exit": "They left their usual area unexpectedly; a caregiver followed up straight away.",
  "No Movement": "They were still for longer than usual, so we sent a check-in to make sure everything is okay.",
  "Door Open": "A door was open at an unusual time; staff checked and everything was secure.",
  "Low SpO₂": "One of their health readings (oxygen) was lower than we like, so the care team is watching it closely.",
  "High HR": "Their heart rate ran a little high, so we're keeping a closer eye on it.",
  "High Temp": "They ran a slight temperature, so we're checking in more often.",
  "Elevated BP": "A blood-pressure reading came in high; a recheck is already scheduled.",
  "Hydration Watch": "They're a bit behind on drinking water in this heat, so extra reminders are going out.",
  "Missed Check-in": "One scheduled check-in was missed; a caregiver is following up.",
  "Low Activity": "They were less active than usual; we're encouraging a bit more movement.",
});

function sitrepDevelopments(client, cadence) {
  const meta = SITREP_CADENCE_META[cadence];
  const slot = SITREP_BADGE_SLOTS[client.riskReason] || null;
  const off = !client.online;
  const withinDelta = `within baseline ${meta.deltaPhrase}`;
  const outsideDelta = `outside baseline ${meta.deltaPhrase}`;

  const category = (key, title, stableText, stableSources) => {
    if (off) {
      // A device-offline window forces LOW confidence (the engine AC) — every
      // telemetry-backed category is honest about the gap instead of guessing.
      return {
        id: `${key}-offline`,
        title,
        priority: "watch",
        text: `${title} status unverified — devices are not reporting (no signal since ${client.lastContact}). Staff checks are in effect.`,
        delta: `no data ${meta.deltaPhrase}`,
        source: "telemetry",
        deviceOffline: true,
        sources: ["device uptime monitor", "gateway status"],
      };
    }
    if (slot && slot.category === key) {
      return {
        id: `${key}-${client.riskReason}`,
        title,
        priority: slot.priority,
        text: SITREP_BADGE_TEXT[client.riskReason],
        delta: slot.priority === "critical" ? "new event — immediate attention" : outsideDelta,
        source: key === "routine" ? "logs" : "telemetry",
        sources:
          key === "routine"
            ? ["reminder completion log", "caregiver check-in log"]
            : ["wearable telemetry", "home sensors"],
      };
    }
    return {
      id: `${key}-stable`,
      title,
      priority: "stable",
      text: stableText,
      delta: withinDelta,
      source: key === "routine" ? "logs" : "telemetry",
      sources: stableSources,
    };
  };

  const windowPhrase = meta.windowLabel.toLowerCase();
  return [
    category(
      "safety",
      "Safety & mobility",
      `No falls detected. Movement and gait within the usual range over the ${windowPhrase}.`,
      ["wearable telemetry", "fall-risk model", "home motion sensors"],
    ),
    category(
      "vitals",
      "Vitals & wellness",
      `Vitals within ${firstNameOf(client.name)}'s usual range; no wellness flags raised over the ${windowPhrase}.`,
      ["wearable telemetry", "vitals monitor"],
    ),
    category(
      "routine",
      "Reminders & check-ins",
      "Scheduled reminders and check-ins completed on time; no misses in the window.",
      ["reminder completion log", "caregiver check-in log"],
    ),
    {
      // Mood is pattern-inferred (MODERATE confidence) even when devices are
      // healthy — the confidence legend's middle rung, per the mockup.
      id: "mood",
      title: "Mood & engagement",
      priority: slot && slot.category === "mood" ? slot.priority : "stable",
      text:
        slot && slot.category === "mood"
          ? SITREP_BADGE_TEXT[client.riskReason]
          : off
            ? "No interaction data this window — engagement cannot be inferred while devices are offline."
            : `Engagement within the usual pattern (${client.activity.toLowerCase()}); check-in responses at typical latency.`,
      delta: slot && slot.category === "mood" ? outsideDelta : withinDelta,
      source: "pattern",
      ...(off ? { deviceOffline: true } : {}),
      sources: ["interaction pattern inference", "caregiver notes"],
    },
    off
      ? {
          id: "connectivity-offline",
          title: "Devices & connectivity",
          priority: "watch",
          text: `No signal since ${client.lastContact}. Emergency detection is degraded until devices are back online; in-person checks active.`,
          delta: `uptime 0% ${meta.deltaPhrase}`,
          source: "telemetry",
          deviceOffline: true,
          sources: ["device uptime monitor", "gateway status"],
        }
      : {
          id: "connectivity-stable",
          title: "Devices & connectivity",
          priority: "stable",
          text: `All ${client.deviceCount} device(s) reporting; no sync gaps or alert-delivery failures in the window.`,
          delta: withinDelta,
          source: "telemetry",
          sources: ["device uptime monitor", "gateway status"],
        },
  ];
}

function firstNameOf(name) {
  return String(name || "").split(" ")[0] || "This client";
}

function sitrepBluf(client, developments, meta) {
  const first = firstNameOf(client.name);
  const critical = developments.filter((d) => d.priority === "critical");
  const watch = developments.filter((d) => d.priority === "watch");
  if (critical.length) {
    return `${first} needs attention now: ${critical
      .map((d) => d.title.toLowerCase())
      .join(", ")} — response underway. ${watch.length} additional item(s) on watch over the ${meta.windowLabel.toLowerCase()}.`;
  }
  if (watch.length) {
    return `${first} is generally steady over the ${meta.windowLabel.toLowerCase()}, with ${watch.length} item(s) to keep watch on: ${watch
      .map((d) => d.title.toLowerCase())
      .join(", ")}. No falls or safety incidents in the window.`;
  }
  return `${first} had a steady ${meta.periodWord} — reminders, movement, vitals, and devices all within the usual range. No action needed.`;
}

function sitrepActions(status) {
  if (status === "red") {
    return {
      now: "Confirm responder arrival, verify the client is safe, and update the family contact.",
      today: "Keep the escalation tree warm and recheck vitals on the tightened interval through the shift.",
      later: "Case review within 72 hours: confirm the trigger, adjust thresholds or the care plan if warranted.",
    };
  }
  if (status === "amber") {
    return {
      now: "Review the watch items with the on-shift caregiver and confirm any unacknowledged prompts.",
      today: "Nora evening check-in; verify the flagged readings or misses have not repeated.",
      later: "Trend-monitor the watch items; if any persists 3 windows, schedule a care-plan review.",
    };
  }
  return {
    now: "No immediate action required.",
    today: "Standard check-in cadence continues.",
    later: "Next scheduled report unless conditions change.",
  };
}

function sitrepFamily(client, developments, status, cadence) {
  const meta = SITREP_CADENCE_META[cadence];
  const first = firstNameOf(client.name);
  const off = !client.online;
  const titles = {
    daily: `How ${first} is doing today`,
    weekly: `${first}'s week in review`,
    monthly: `${first}'s month in review`,
  };
  // One plain-language watch line per flagged concern: the client's badge
  // (if any) plus the offline note — never clinician wording.
  const watchItems = [];
  if (SITREP_FAMILY_WATCH_TEXT[client.riskReason]) {
    watchItems.push(SITREP_FAMILY_WATCH_TEXT[client.riskReason]);
  }
  if (off) {
    watchItems.push(
      `${first}'s devices stopped reporting, so a caregiver is checking in person until they're back online.`,
    );
  }
  const well = [];
  if (!off) {
    const stable = new Set(
      developments.filter((d) => d.priority === "stable").map((d) => d.id),
    );
    if (stable.has("safety-stable")) well.push(`No falls — ${first} moved around normally.`);
    if (stable.has("vitals-stable")) well.push("Health readings looked the way they usually do.");
    if (stable.has("routine-stable")) well.push("Reminders and check-ins went as planned.");
    if (stable.has("connectivity-stable")) well.push("All devices are working and reporting.");
  }
  if (!well.length) {
    well.push(`The care team stayed in close touch with ${first} through the ${meta.periodWord}.`);
  }
  const bluf =
    status === "red"
      ? `Something needed attention this ${meta.periodWord} — details below. The care team responded right away and is staying with it; we'll keep you updated.`
      : status === "amber"
        ? `${first} is doing okay overall. A few things came up that we're keeping an eye on — details below.`
        : `${first} had a steady ${meta.periodWord}. Reminders, movement, and devices all looked the way they usually do.`;
  return {
    title: titles[cadence],
    status:
      status === "red"
        ? "We need your attention today"
        : status === "amber"
          ? "Doing okay — a few things we're keeping an eye on"
          : "Doing well",
    bluf,
    well,
    watch: watchItems.length ? watchItems : ["Nothing right now — we'll flag anything new here."],
    doing: [
      `The care team reviews ${first}'s updates every shift.`,
      status === "green"
        ? "Regular check-ins continue on the usual schedule."
        : "Check-ins are happening more often until things settle.",
    ],
    help: [
      "A short call often brightens the day — evenings work best.",
      `Tell us if anything seems different to you — you know ${first} best.`,
    ],
    confidence: off
      ? `${first}'s devices were offline for part of this period, so we're less certain than usual. A caregiver is confirming things in person.`
      : `Most of this comes straight from ${first}'s devices and reminder logs, so we're quite confident. Mood is our best read from how ${first} interacted — treat that part as an impression, not a fact.`,
  };
}

function sitrepHighlights(client, meta) {
  const off = !client.online;
  return [
    {
      label: "Daily Living",
      text: off
        ? "Routine status unverified while devices are offline — staff checks are standing in."
        : "Routine tasks performed with standard prompts; no increase in support needs this window.",
    },
    {
      label: "Communication Status",
      text: off
        ? "No app or device contact this window; last contact " + client.lastContact + "."
        : "Check-in responses at a healthy rate for the window; no unanswered inbound messages.",
    },
    {
      label: "Technology Performance",
      text: off
        ? "Devices not reporting — alert delivery cannot be verified until connectivity is restored."
        : "App, wearable, and home sensors nominal; alert delivery healthy for the " + meta.windowLabel.toLowerCase() + ".",
    },
  ];
}

function sitrepRisks(client, status) {
  const off = !client.online;
  return [
    {
      area: "Client Safety",
      status: status === "red" ? "ELEVATED" : status === "amber" ? "MODERATE" : "LOW",
      tone: status === "red" ? "critical" : status === "amber" ? "caution" : "normal",
      why:
        status === "red"
          ? "Active incident — response underway with full monitoring coverage."
          : status === "amber"
            ? "Watch items exceed baseline; monitoring coverage full."
            : "No safety events; monitoring coverage full.",
    },
    {
      area: "Routine & Medication",
      status: client.riskReason === "Missed Check-in" ? "MODERATE" : "STABLE",
      tone: client.riskReason === "Missed Check-in" ? "caution" : "normal",
      why:
        client.riskReason === "Missed Check-in"
          ? "A check-in miss exceeds the usual pattern; follow-up queued."
          : "Reminders and scheduled tasks tracking to plan.",
    },
    {
      area: "Device & Connectivity",
      status: off ? "MODERATE" : "STABLE",
      tone: off ? "caution" : "normal",
      why: off
        ? "Devices offline — emergency-detection latency is degraded until restored."
        : "Uptime healthy; no alert-delivery failures.",
    },
  ];
}

function sitrepWatchIndicators(client, developments) {
  const flagged = developments.filter(
    (d) => d.priority === "critical" || d.priority === "watch",
  );
  if (!flagged.length) {
    return [
      {
        k: "Routine deviation",
        t: "Any change in reminder completion or check-in response into the next reporting window.",
      },
    ];
  }
  return flagged.map((d) => ({
    k: d.title,
    t: `Whether this remains outside ${firstNameOf(client.name)}'s usual pattern into the next reporting window.`,
  }));
}

/**
 * GMA-1624 — demo SITRep board: one report per demo client, three cadences.
 * Structure (not prose) is the contract — see the block comment above.
 */
export function getDemoSitrep(now = Date.now()) {
  const clients = getDemoClients(now);
  return {
    generatedAt: now,
    feeds: [
      "reminder completion",
      "medication prompts",
      "wearable telemetry",
      "movement/location trends",
      "fall-risk signals",
      "caregiver call & check-in logs",
      "CompassCare alerts",
      "sleep signals",
      "device uptime",
      "environmental sensors",
      "calendar adherence",
      "staff/family notes",
    ],
    confidenceLegend: [
      { level: "high", text: "direct device telemetry, confirmed caregiver input, completed task logs" },
      { level: "moderate", text: "pattern-based inference from behavior, usage, and timing" },
      { level: "low", text: "limited data window, temporary connectivity loss, conflicting signals" },
    ],
    clients: clients.map((client) => ({
      clientId: client.id,
      clientName: client.name,
      age: client.age,
      home: client.home,
      room: client.room,
      livingEnv: client.livingEnv,
      online: client.online,
      cadences: Object.fromEntries(
        Object.entries(SITREP_CADENCE_META).map(([key, meta]) => {
          const developments = sitrepDevelopments(client, key);
          // Roll up with the REAL engine so the fixture can never drift from
          // what the masthead computes at render time.
          const status = overallStatus(developments);
          return [
            key,
            {
              key,
              label: meta.label,
              mastTitle: meta.mastTitle,
              windowLabel: meta.windowLabel,
              windowMs: windowMsForCadence(key),
              bluf: sitrepBluf(client, developments, meta),
              developments,
              highlights: sitrepHighlights(client, meta),
              risks: sitrepRisks(client, status),
              watch: sitrepWatchIndicators(client, developments),
              actions: sitrepActions(status),
              family: sitrepFamily(client, developments, status, key),
            },
          ];
        }),
      ),
    })),
  };
}
/* ---------------------------- end GMA-1624 -------------------------------- */
